export function resolveNotificationTenderId(notification) {
  if (!notification || typeof notification !== "object") {
    return null;
  }

  const candidates = [
    notification?.TenderId?.public_id,
    notification?.payload?.TenderId?.public_id,
    notification?.Payload?.TenderId?.public_id,
    notification?.TenderId?.ID,
    notification?.TenderId?.Id,
    notification?.tender_zoho_id,
    notification?.tender_id,
    notification?.payload?.TenderId?.ID,
    notification?.payload?.TenderId?.Id,
    notification?.payload?.tender_id,
    notification?.Payload?.TenderId?.ID,
    notification?.Payload?.tender_id,
  ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && candidate !== "") {
      return String(candidate);
    }
  }

  return null;
}

function resolveNotificationType(notification) {
  if (!notification || typeof notification !== "object") {
    return "";
  }

  const candidates = [
    notification?.notification_type,
    notification?.Notification_Type,
    notification?.notificationType,
    notification?.type,
    notification?.payload?.notification_type,
    notification?.payload?.Notification_Type,
    notification?.payload?.type,
    notification?.Payload?.notification_type,
    notification?.Payload?.type,
  ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined) {
      const value = String(candidate).trim();
      if (value !== "") return value.toLowerCase();
    }
  }

  return "";
}

function resolveNotificationLocalTenderId(notification) {
  if (!notification || typeof notification !== "object") {
    return null;
  }

  const candidates = [
    notification?.TenderId?.public_id,
    notification?.payload?.TenderId?.public_id,
    notification?.Payload?.TenderId?.public_id,
    notification?.tender_id,
    notification?.TenderId?.local_id,
    notification?.payload?.tender_id,
    notification?.payload?.TenderId?.local_id,
    notification?.Payload?.tender_id,
  ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && candidate !== "") {
      return String(candidate);
    }
  }

  return null;
}

export function resolveNotificationTitle(notification, lang = "en") {
  if (!notification || typeof notification !== "object") {
    return "";
  }

  const normalizedLang = String(lang || "").toLowerCase();

  const candidates =
    normalizedLang === "ar"
      ? [
          notification?.Title_ar,
          notification?.Title_AR,
          notification?.TitleAr,
          notification?.title_ar,
          notification?.payload?.Title_ar,
          notification?.payload?.title_ar,
        ]
      : normalizedLang === "zh-hans"
        ? [
            notification?.Title_zh_hans,
            notification?.Title_ZH_HANS,
            notification?.TitleZhHans,
            notification?.title_zh_hans,
            notification?.payload?.Title_zh_hans,
            notification?.payload?.title_zh_hans,
          ]
        : [
            notification?.Title_en,
            notification?.Title_EN,
            notification?.TitleEn,
            notification?.title_en,
            notification?.payload?.Title_en,
            notification?.payload?.title_en,
          ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined) {
      const value = String(candidate).trim();
      if (value !== "") return value;
    }
  }

  const fallback =
    notification?.Title ??
    notification?.title ??
    notification?.payload?.Title ??
    notification?.Title_en ??
    notification?.title_en ??
    notification?.payload?.Title_en ??
    notification?.Title_ar ??
    notification?.title_ar ??
    notification?.payload?.Title_ar ??
    notification?.Title_zh_hans ??
    notification?.title_zh_hans ??
    notification?.payload?.Title_zh_hans;
  return fallback ? String(fallback).trim() : "";
}

export function resolveNotificationTargetRoute(notification, userCompanyId) {
  const notificationType = resolveNotificationType(notification);
  const contractId =
    notification?.payload?.contract_public_id ||
    notification?.Payload?.contract_public_id ||
    notification?.payload?.contract_id ||
    notification?.Payload?.contract_id ||
    null;

  if (notificationType === "contract_supplier_review_required") {
    return contractId
      ? `/financial-claims/contracts/${contractId}`
      : "/financial-claims/contracts/incoming";
  }

  const explicitRoute =
    notification?.route ||
    notification?.Route ||
    notification?.payload?.route ||
    notification?.payload?.Route ||
    notification?.Payload?.route ||
    notification?.Payload?.Route;

  if (explicitRoute && String(explicitRoute).trim() !== "") {
    return String(explicitRoute).trim();
  }

  if (notificationType === "support ticket reply") {
    const ticketNumber =
      notification?.payload?.support_ticket_number ||
      notification?.Payload?.support_ticket_number ||
      notification?.support_ticket_number ||
      null;
    return ticketNumber
      ? `/communication-management?ticket=${encodeURIComponent(ticketNumber)}`
      : "/communication-management";
  }

  if (notificationType === "participation requested") {
    return "/incoming-participation-requests";
  }

  if (
    notificationType === "pending approval" ||
    notificationType === "tender approval" ||
    notificationType === "tender approval requested"
  ) {
    return "/tender-approvals";
  }

  if (notificationType === "participation accepted") {
    const localTenderId = resolveNotificationLocalTenderId(notification);
    return localTenderId ? `/submitOffer/${localTenderId}/active` : "/my-offers";
  }

  if (notificationType === "negotiation responded") {
    const negotiationId =
      notification?.payload?.negotiation_request_public_id ||
      notification?.Payload?.negotiation_request_public_id ||
      notification?.payload?.negotiation_request_id ||
      notification?.Payload?.negotiation_request_id ||
      null;
    if (negotiationId) {
      return `/publisher-negotiations/${negotiationId}`;
    }
    return "/publisher-negotiation-responses";
  }

  if (notificationType === "negotiation request") {
    return "/incoming-negotiation-requests";
  }

  if (notificationType === "financial claim submitted") {
    const claimId =
      notification?.payload?.financial_claim_public_id ||
      notification?.Payload?.financial_claim_public_id ||
      notification?.payload?.financial_claim_id ||
      notification?.Payload?.financial_claim_id ||
      null;
    return claimId ? `/incoming-financial-claims/${claimId}` : "/incoming-financial-claims";
  }

  if (notificationType.startsWith("financial claim")) {
    const claimId =
      notification?.payload?.financial_claim_public_id ||
      notification?.Payload?.financial_claim_public_id ||
      notification?.payload?.financial_claim_id ||
      notification?.Payload?.financial_claim_id ||
      null;
    return claimId ? `/financial-claims/${claimId}` : "/financial-claims";
  }

  const tenderId = resolveNotificationTenderId(notification);
  if (!tenderId) return "/notifications";

  const candidates = [
    notification?.TenderId?.OwnerCompanyId,
    notification?.TenderId?.owner_company_id,
    notification?.TenderId?.CompanyId,
    notification?.TenderId?.company_id,
    notification?.payload?.TenderId?.OwnerCompanyId,
    notification?.payload?.TenderId?.owner_company_id,
  ];

  let ownerCompanyId = null;
  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && candidate !== "") {
      ownerCompanyId = String(candidate);
      break;
    }
  }

  const isOwner =
    ownerCompanyId !== null &&
    userCompanyId !== null &&
    userCompanyId !== undefined &&
    String(ownerCompanyId) === String(userCompanyId);

  return isOwner ? `/mytender/${tenderId}/view` : `/tender/${tenderId}/view`;
}

export function isNotificationUnreadForUser(notification, userUuid) {
  if (!notification || typeof notification !== "object") {
    return false;
  }

  if (!userUuid) {
    return false;
  }

  const seenStr = notification?.Seen || "";
  const seenArr =
    seenStr === "" ? [] : String(seenStr).split(",").filter(Boolean);

  return !seenArr.includes(String(userUuid));
}
